import { z } from 'zod'
import type { PedidoDeTaco, TacoEmJogo } from '@sol-pool/tacos'
import { NICK_MAX } from './apelido'
import { INVITE_LENGTH } from './convite'
import { EventoSchema } from './eventos'
import { MensagensDoClienteDeEvento, MensagensDoServidorDeEvento } from './eventos-de-mesa'
import { TETO_DIGITOS_VALOR } from './valores'
import { ROOM_PASSWORD_MAX } from './senha'

/**
 * Contrato de mensagens entre cliente e servidor.
 *
 * Este pacote é a única fonte da verdade do protocolo — os dois lados importam
 * daqui e validam com os mesmos schemas. Toda mensagem que chega do cliente é
 * parseada antes de ser olhada; nada de `as` em payload de rede.
 *
 * Valores trafegam como STRING de unidades inteiras (lamports), nunca como
 * number. `number` em JS perde precisão acima de 2^53.
 *
 * PONTO CENTRAL DO DESENHO: o servidor não move dinheiro. Quem assina o
 * depósito é a carteira do jogador. O servidor apenas LÊ a chain e confirma
 * (docs/TDD.md §6.4). Por isso criar uma mesa tem dois passos — reservar e
 * confirmar — com a assinatura da carteira no meio.
 */

/** SOL usa 9 casas (1 SOL = 1e9 lamports). */
export const DECIMALS = 9

/** Estados da sala. Espelha a máquina de estados do TDD §5. */
export const RoomState = z.enum([
  /** Criador depositou on-chain, esperando oponente. */
  'waiting',
  /** Os dois depositaram; a partida vai começar. */
  'committed',
  /** Partida em andamento. */
  'playing',
  /** Acabou; aguardando liquidação on-chain. */
  'settled',
])
export type RoomState = z.infer<typeof RoomState>

/**
 * Modalidade da mesa.
 *
 * Fica na SALA, não na partida, porque quem entra precisa saber o que vai
 * jogar antes de depositar. Descobrir a modalidade só depois de o dinheiro
 * estar no contrato seria uma armadilha.
 */
export const GameMode = z.enum(['eightball', 'sinuca'])
export type GameMode = z.infer<typeof GameMode>

// O alcance da linha-guia mora em `mira.ts`, que a sala e o evento de mesa
// compartilham; reexportado aqui porque este é o ponto de entrada do pacote.
export { MIRA_INFINITA, MIRA_PADRAO_M, MIRA_TETO_M, MiraM } from './mira'
import { MIRA_PADRAO_M, MiraM } from './mira'

export const Room = z.object({
  id: z.string(),
  label: z.string(),
  mode: GameMode,
  creator: z.string(),
  opponent: z.string().nullable(),
  /** Valor da entrada por jogador, em lamports. */
  stake: z.string(),
  state: RoomState,
  /** match_id on-chain, 32 caracteres hex (16 bytes). */
  matchId: z.string(),
  createdAt: z.number(),
  /** Momento em que a sala sai da listagem se ninguém entrar. */
  expiresAt: z.number(),
  /**
   * A mesa aceita espectadores?
   *
   * Decidido por quem CRIA, antes de depositar, e imutável depois: quem entra
   * sabe que pode estar sendo assistido, e quem assiste sabe que foi convidado.
   * Desligado por padrão — uma partida com dinheiro em jogo é, à falta de
   * escolha, uma conversa entre dois.
   */
  spectators: z.boolean(),
  /**
   * A mesa tem voz?
   *
   * Decidido por quem CRIA, como `spectators`, e imutável depois. Os dois
   * jogadores falam; quem assiste escuta. Desligado por padrão — uma partida
   * com dinheiro em jogo não abre microfone sem alguém pedir.
   */
  voice: z.boolean().default(false),
  /**
   * Quanto da linha-guia a mesa mostra.
   *
   * Decidido por quem CRIA, como `spectators` e `voice`, e imutável depois:
   * ver a trajetória da bola-objeto até a caçapa é informação que o adversário
   * não tem, e estas mesas têm aposta em SOL. Os dois jogam no mesmo modo, e
   * quem entra vê o valor na listagem ANTES de depositar.
   *
   * O default é o jogo de hoje: sala criada antes deste campo, ou por um
   * cliente antigo, continua sendo exatamente a mesa que sempre foi.
   */
  miraM: MiraM.default(MIRA_PADRAO_M),
  /**
   * A mesa tem senha?
   *
   * Só o BIT viaja. O hash fica no servidor e a senha em claro não fica em
   * lugar nenhum: o lobby precisa saber que vai pedir uma senha — para
   * desenhar o cadeado e o campo — e nada além disso.
   */
  protected: z.boolean().default(false),
  /**
   * Código curto de convite (ver `convite.ts`): seis caracteres, único entre
   * as salas vivas. É o que vai num convite; o `matchId` fica para o contrato.
   * `default('')` para um servidor antigo, que não o manda, ainda parsear —
   * a tela trata vazio como "sem código".
   */
  code: z.string().default(''),
  /**
   * Apelidos dos dois, ou nulos quando não escolheram um. Derivados do perfil
   * no servidor a cada envio — não são gravados na sala. A tela mostra o
   * apelido SEMPRE com o endereço abreviado ao lado: apelido não é único.
   */
  creatorName: z.string().nullable().default(null),
  opponentName: z.string().nullable().default(null),
  /**
   * Quando os depósitos voltam sozinhos, em ms desde a época: o prazo da conta
   * `Game` na chain. Passado dele, o servidor devolve a entrada de cada um.
   * Serve para a tela dizer a quem ficou preso numa partida que não começou
   * QUANDO o dinheiro volta. Nulo para servidor antigo ou prazo desconhecido.
   */
  refundAt: z.number().nullable().default(null),
})
export type Room = z.infer<typeof Room>

/**
 * O taco com que se joga uma partida: os números e a arte.
 *
 * Os limites não são revalidados aqui de propósito — quem os aplica é
 * `clampCue`, no motor, dos dois lados. Um schema que também os conhecesse
 * viraria uma segunda cópia da regra, e duas cópias divergem.
 */
export const CueEmJogo = z.object({
  id: z.string().max(32),
  massBps: z.number().int(),
  spinBps: z.number().int(),
  aimBps: z.number().int(),
  clothGripBps: z.number().int(),
  breakBonusBps: z.number().int(),
  /** Caminho da arte do taco. Opcional: cliente antigo não a lê e desenha a sua própria. */
  skin: z.string().max(128).optional(),
})

/** O taco com que se joga uma partida: os números da física mais a arte. */
export type CueEmJogo = z.infer<typeof CueEmJogo>

/**
 * O `id` de um taco gratuito é `CueId` (união de literais) do lado do
 * pacote `@sol-pool/tacos`, mas o schema valida só o FORMATO da mensagem —
 * string até 32 — e não se o id existe no catálogo; essa checagem é do
 * servidor, que tem o catálogo (mesmo espírito de `CueEmJogo`, que não
 * revalida os bps). Por isso o `as z.ZodType<..., unknown>`: sem ele,
 * `z.string()` (Output largo) não `satisfies` um campo tipado como `CueId`
 * (Output estreito) — um choque de variância do TypeScript, não um bug no
 * schema. O `unknown` como terceiro parâmetro (o `Input`) é o que faz
 * `exactOptionalPropertyTypes` aceitar a asserção sem reclamar do restante
 * da forma.
 */
type IdDoPedido = Extract<PedidoDeTaco, { id: unknown }>['id']
const IdDoPedidoSchema = z.string().max(32) as z.ZodType<IdDoPedido, z.ZodTypeDef, unknown>

/** O que o cliente pede. Por id (gratuito) ou por mint (NFT). Quem decide é o servidor. */
export const PedidoDeTacoSchema = z.union([
  z.object({ id: IdDoPedidoSchema }),
  z.object({ mint: z.string().min(32).max(64) }),
]) satisfies z.ZodType<PedidoDeTaco, z.ZodTypeDef, unknown>

const PontosSchema = z.object({ peso: z.number(), efeito: z.number(), aderencia: z.number(), quebra: z.number() })

/** `arquetipo` é `CueId` em `PerfilDeTaco` — mesmo choque de variância de `IdDoPedidoSchema`. */
type ArquetipoDoPerfil = TacoEmJogo['perfil']['arquetipo']
const ArquetipoSchema = z.string().max(32) as z.ZodType<ArquetipoDoPerfil, z.ZodTypeDef, unknown>

const PerfilSchema = z.object({
  // `gratuito` incluída: é a raridade do taco que vem com a carteira, e o
  // servidor manda esse taco por este mesmo schema. Ela NÃO é cunhável — quem
  // guarda essa fronteira é `lerPerfilPadrao`, na leitura da chain.
  raridade: z.enum(['gratuito', 'common', 'uncommon', 'rare', 'epic', 'legendary']),
  arquetipo: ArquetipoSchema,
  seed: z.number().int().nonnegative(),
  geracao: z.number().int().nonnegative(),
})
const ParamsSchema = z.object({ massBps: z.number().int(), spinBps: z.number().int(), aimBps: z.number().int(), clothGripBps: z.number().int(), breakBonusBps: z.number().int() })

/** Mesmo choque de variância de `IdDoPedidoSchema`, agora para o `id` de `TacoEmJogo`. */
type IdDoTaco = Extract<TacoEmJogo, { origem: 'gratuito' }>['id']
const IdDoTacoSchema = z.string().max(32) as z.ZodType<IdDoTaco, z.ZodTypeDef, unknown>

/**
 * Os quatro atributos de um taco.
 *
 * Repetidos aqui em vez de importados de `@sol-pool/tacos` porque este módulo
 * só importa TIPOS de lá — o protocolo não carrega o pacote de regras em
 * tempo de execução. O `satisfies` amarra a lista ao tipo: sumir com um
 * atributo, ou inventar um, quebra a compilação aqui.
 *
 * A `mira` era o terceiro e saiu na física v6: todo taco aponta na mesma
 * resolução, então não há o que pontuar nem o que vender na loja de upgrade.
 * Um cliente antigo que ainda mande `atributo: 'mira'` é recusado pelo
 * `z.enum` — que é o comportamento certo, porque o nível não teria efeito.
 */
type AtributoDoTaco = keyof TacoEmJogo['pontos']
export const ATRIBUTO_DO_TACO = [
  'peso',
  'efeito',
  'aderencia',
  'quebra',
] as const satisfies readonly AtributoDoTaco[]
const AtributoSchema = z.enum(ATRIBUTO_DO_TACO)

/** Um objeto com os quatro atributos como chaves. */
const porAtributo = <T extends z.ZodTypeAny>(v: T) =>
  z.object({ peso: v, efeito: v, aderencia: v, quebra: v })

/**
 * A loja de upgrade que viaja junto do taco.
 *
 * `preco` é string de dígitos e não `number`: são lamports, e acima de 2^53 o
 * `number` do JS erra a conta — a mesma razão de `tacos.mint.estado`. O
 * `motivo` viaja como código, não como frase: quem traduz é o cliente, pelo
 * catálogo de eventos.
 */
export const UpgradeSchema = z.object({
  niveis: porAtributo(z.number().int().nonnegative()),
  gastos: z.number().int().nonnegative(),
  orcamento: z.number().int().nonnegative(),
  /** O teto de níveis de UM atributo, para o cartão escrever "nv 1/5". */
  nivelMaximo: z.number().int().nonnegative(),
  preco: porAtributo(z.string().regex(/^\d+$/).nullable()),
  motivo: porAtributo(z.enum(['orcamento', 'nivelMaximo', 'tetoDoAtributo']).nullable()),
  /**
   * O total já pago por este taco, em lamports. Mesma forma do `preco`, e
   * pelo mesmo motivo: `BigInt` lança no que não for dígito, e quem o chama é
   * o render do lobby — um valor torto derrubaria a tela inteira.
   */
  investidoLamports: z.string().regex(/^\d+$/),
})

/**
 * Um taco pronto, como o servidor o calculou. O cliente desenha; não deriva.
 * Os bps vão junto dos pontos porque é com os bps que a mesa é armada, e
 * duas derivações (uma em cada lado) divergiriam na primeira versão diferente.
 */
export const TacoEmJogoSchema = z.intersection(
  z.union([
    z.object({ origem: z.literal('gratuito'), id: IdDoTacoSchema }),
    z.object({ origem: z.literal('nft'), mint: z.string().max(64), colecao: z.string().max(64) }),
    // NFT de coleção parceira: mesma dupla mint+coleção do NFT próprio, mais o
    // `numero` — a posição na tabela que decidiu raridade/arquétipo/arte.
    z.object({
      origem: z.literal('parceira'),
      mint: z.string().max(64),
      colecao: z.string().max(64),
      numero: z.number().int().nonnegative(),
    }),
  ]),
  z.object({ perfil: PerfilSchema, pontos: PontosSchema, params: ParamsSchema, skin: z.string().max(128), upgrade: UpgradeSchema.nullable() }),
) satisfies z.ZodType<TacoEmJogo, z.ZodTypeDef, unknown>

/** Os apelidos dos dois jogadores, na ordem dos índices. Nulo onde não há. */
const PlayerNames = z.tuple([z.string().nullable(), z.string().nullable()])
export type PlayerNames = z.infer<typeof PlayerNames>

// ------------------------------------------------------------------ conversa

/**
 * Limites da conversa da mesa, compartilhados com o cliente.
 *
 * O cliente os usa para recusar ANTES de mandar — a caixa de texto para de
 * aceitar letras no teto, e o botão espera o intervalo. O servidor os aplica de
 * novo porque o cliente é hostil por definição; os dois lendo a mesma constante
 * é o que impede a tela de prometer o que o servidor recusa.
 */
export const CHAT_MAX_CHARS = 200
/** Uma mensagem por segundo, por sessão. */
export const CHAT_INTERVALO_MS = 1_000
/** Quantas mensagens a tela guarda. Conversa, não ata: o resto some. */
export const CHAT_HISTORICO = 50

// ------------------------------------------------------------ tetos de texto

/**
 * Teto de tamanho de cada campo de TEXTO que chega do cliente.
 *
 * Nenhum destes números é regra de jogo — é o tamanho real do campo com folga
 * larga. Existem porque um `z.string()` solto aceita megabytes: o servidor
 * parseava e guardava o que viesse, e um socket anônimo conseguia empurrar
 * mensagens enormes antes de qualquer checagem de sessão. Derrubar o servidor
 * não é cosmético aqui: partida que não liquida até o prazo cai no
 * `claim_timeout` e vira empate, com o vencedor recebendo só a entrada de
 * volta em vez do pote.
 *
 * De onde saem os números:
 *
 *   endereço  44 caracteres (chave pública Solana em base58)
 *   assinatura 88 (64 bytes ed25519 em base58)
 *   nonce     36 (`randomUUID`, ver `auth.ts`)
 *   token     72 (dois `randomUUID` concatenados, ver `issueSession`)
 *   id        64 (o `match_id` em hex — é ele que serve também de `roomId`)
 *   mensagem  a prosa fixa de `buildLoginMessage` (~180) mais host, endereço
 *             e nonce
 *   valor     20 dígitos cobrem 2^64 em lamports; nada legítimo passa disso
 *
 * A folga (grosso modo, o dobro) é deliberada: estes tetos não devem ser o que
 * recusa um campo quase certo. Quem os estoura está mandando lixo, e
 * `bad_request` é a resposta honesta.
 */
export const TETO_ENDERECO = 64
export const TETO_ASSINATURA = 128
export const TETO_NONCE = 64
export const TETO_TOKEN = 160
export const TETO_ID = 128
export const TETO_MENSAGEM_LOGIN = 512

/**
 * Os campos que o JOGADOR digita têm teto próprio, e mais frouxo.
 *
 * Mesma escolha já feita em `chat.send`: o limite que ele vê — senha, apelido —
 * é conferido no servidor, que recusa com o evento que a tela sabe explicar.
 * Se o schema barrasse no limite exato, um caractere a mais viraria
 * `bad_request` genérico. O múltiplo por cinco deixa a regra de jogo falar
 * primeiro e ainda barra o megabyte.
 */
export const TETO_SENHA = ROOM_PASSWORD_MAX * 5
export const TETO_APELIDO = NICK_MAX * 5
export const TETO_CODIGO = INVITE_LENGTH * 8

export const ErrorCode = z.enum([
  'unauthenticated',
  'bad_request',
  'nonce_invalid',
  'signature_invalid',
  'stake_out_of_range',
  'deposit_not_found',
  'deposit_mismatch',
  'room_not_found',
  'room_not_joinable',
  'already_in_room',
  'not_room_creator',
  'no_reservation',
  'chain_error',
  'rate_limited',
  'internal',

  // Voz
  'voice_off',
  'voice_not_allowed',
  'voice_unavailable',
  /**
   * O motor do navegador não passou pela verificação de determinismo.
   *
   * Código próprio para o cliente poder tratá-lo como o que é — uma limitação
   * conhecida, com caminho alternativo — em vez de um erro genérico que soa
   * como defeito.
   */
  'engine_unverified',

  // Partida
  'match_not_found',
  'not_your_turn',
  'wrong_phase',
  'bad_shot',
  'bad_decision',
  'unknown_player',
  'already_revealed',
  'bad_reveal',
  // O compromisso anunciado não bate com o que está na blockchain. Recusado
  // antes da primeira tacada — ver `conferirCommitContraChain` no servidor.
  'commit_mismatch',

  // Espectadores
  /** A sala não permite assistir; quem criou decidiu assim. */
  'spectators_disabled',

  // Senha da sala
  /** A sala tem senha e o pedido veio sem ela. */
  'password_required',
  /** Senha não confere. */
  'password_incorrect',
  /** Fora de `ROOM_PASSWORD_MIN..MAX` (ao criar ou ao tentar). */
  'password_invalid',
  /** Errou demais em pouco tempo; espere. */
  'password_attempts',

  // Convite por código
  /** Nenhuma sala viva com este código. */
  'invite_not_found',
  /** Não tem a forma de um código (seis do alfabeto). */
  'invite_invalid',
  /** Tentou códigos demais em pouco tempo. */
  'invite_attempts',

  // Apelido
  /** Fora da forma: tamanho ou caracteres (ver `apelido.ts`). */
  'nickname_invalid',
  /** Está na lista de palavras proibidas. */
  'nickname_forbidden',
  /** Trocou demais em pouco tempo. */
  'nickname_attempts',

  // Conversa
  /** Passou de `CHAT_MAX_CHARS`. */
  'chat_too_long',
  /** Mandou de novo antes de `CHAT_INTERVALO_MS`. */
  'chat_too_fast',
  /** Só quem está na mesa — jogando ou assistindo — conversa nela. */
  'not_in_match',
  /** Eventos de mesa (melhor de 3, torneio). Ver `eventos-de-mesa.ts`. */
  'evento_nao_encontrado',
  'evento_cheio',
  'evento_fechado',
  'ja_em_evento',
  'fora_do_evento',
  'formato_invalido',
  /** Modo manutenção: o servidor vai ser atualizado e não começa nada novo. */
  'em_manutencao',
])
export type ErrorCode = z.infer<typeof ErrorCode>

// ---------------------------------------------------------------- cliente →

export const ClientMessage = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('auth'),
    address: z.string().max(TETO_ENDERECO),
    nonce: z.string().max(TETO_NONCE),
    message: z.string().max(TETO_MENSAGEM_LOGIN),
    signature: z.string().max(TETO_ASSINATURA),
  }),
  z.object({ t: z.literal('auth.resume'), token: z.string().max(TETO_TOKEN) }),
  z.object({ t: z.literal('lobby.subscribe') }),

  /** Passo 1 de criar: pede um match_id. Nada acontece on-chain ainda. */
  z.object({
    t: z.literal('lobby.reserve'),
    stake: z.string().max(TETO_DIGITOS_VALOR).regex(/^\d+$/),
    label: z.string().max(24).optional(),
    mode: GameMode.optional(),
    /** O alcance da linha-guia. Omitido vale `MIRA_PADRAO_M`, o jogo de hoje. */
    miraM: MiraM.optional(),
    /** Permitir espectadores. Omitido vale `false`. */
    spectators: z.boolean().optional(),
    /** Ligar a voz da mesa. Omitido vale `false`. */
    voice: z.boolean().optional(),
    /**
     * Senha da mesa, em claro, UMA vez: o servidor guarda só o hash. O teto
     * aqui é frouxo de propósito — quem confere o tamanho de verdade é o
     * `Lobby`, com `conferirSenha`, que recusa com o evento certo em vez de um
     * `bad_request` genérico. Ver `TETO_SENHA`.
     */
    password: z.string().max(TETO_SENHA).optional(),
  }),
  /** Passo 2: o depósito já foi assinado e enviado; confirme na chain. */
  z.object({ t: z.literal('lobby.confirmCreate'), matchId: z.string().max(TETO_ID) }),

  /**
   * Passo 1 de entrar: reserva a vaga e devolve o match_id para assinar.
   *
   * A senha vai AQUI, e não no passo 2: é antes de assinar que a recusa não
   * custa nada. A reserva que este passo cria é a prova de que a senha
   * passou, e o `confirmJoin` só a exige.
   */
  z.object({
    t: z.literal('lobby.requestJoin'),
    roomId: z.string().max(TETO_ID),
    password: z.string().max(TETO_SENHA).optional(),
  }),
  /** Passo 2: depósito do oponente assinado; confirme na chain. */
  z.object({ t: z.literal('lobby.confirmJoin'), roomId: z.string().max(TETO_ID) }),

  /** O cancelamento já foi assinado e enviado; sincronize o lobby. */
  z.object({ t: z.literal('lobby.confirmCancel'), roomId: z.string().max(TETO_ID) }),

  // ------------------------------------------------------------- partida

  /**
   * Compromisso com o nonce da quebra.
   *
   * O jogador sorteia 32 bytes em segredo e manda só o hash. Depois que os
   * dois se comprometeram, ambos revelam — e o seed sai do hash dos dois
   * nonces juntos. Sem isso, quem escolhesse o seed escolheria a quebra: com
   * física determinística, é o mesmo que escolher onde as bolas param.
   */
  z.object({
    t: z.literal('match.commit'),
    commit: z.string().regex(/^[0-9a-f]{64}$/),
    /**
     * O taco que este jogador PEDE para usar.
     *
     * Pedido, e não escolha: o taco entra na física e vai gravado no replay,
     * então quem decide é o servidor — ele confere se a carteira pode usá-lo e
     * cai no padrão quando não pode. Ver `apps/server/src/tacos.ts`.
     *
     * Vai no compromisso porque é o último instante antes de a partida
     * existir, e porque é por jogador: cada um traz o seu.
     *
     * Opcional: cliente antigo não manda, e joga com o padrão.
     *
     * Por id ou por mint. A string solta de antes não passa: cliente antigo
     * cai no padrão.
     */
    cue: PedidoDeTacoSchema.optional(),
  }),
  z.object({ t: z.literal('match.reveal'), nonce: z.string().regex(/^[0-9a-f]{64}$/) }),

  /**
   * Para onde o jogador está mirando AGORA, antes de tacar.
   *
   * Puramente cosmético: existe para o adversário ver que alguém está do outro
   * lado se posicionando, em vez de encarar uma tela parada escrito "vez do
   * adversário". NÃO entra no replay, NÃO toca a mesa e NÃO vale como tacada —
   * só o `match.shoot` conta.
   *
   * Em mesa real você vê o outro se abaixar e alinhar; isto é a mesma
   * informação, não uma vantagem nova.
   */
  z.object({
    t: z.literal('match.aim'),
    /** Mesma codificação da tacada: inteiros, não floats. */
    angle: z.number().int().min(0).max(65_535),
    power: z.number().int().min(0).max(255),
    /*
     * A caçapa declarada, enquanto ele ainda mira.
     *
     * Viajava SÓ junto com a tacada, e por isso o adversário descobria o buraco
     * declarado quando a bola já estava andando. Na mesa real a declaração é
     * dita em voz alta ANTES — quem está esperando ouve, e é com isso que
     * decide o que torcer. Aqui é a mesma informação, e ela não dá vantagem
     * nenhuma: quem declara é quem vai jogar.
     *
     * Opcional porque a maioria das tacadas não declara nada.
     */
    pocket: z.number().int().min(0).max(5).nullable().optional(),
  }),

  /**
   * Onde a branca está sendo levada, numa bola na mão, antes de ser posta.
   *
   * Cosmético, como `match.aim`: o servidor só repassa ao adversário e à
   * plateia, para eles verem a branca se arrastando. A posição que vale é a do
   * `match.place`. Os limites são folgados de propósito — a mesa tem 1,98 m —,
   * só para um número absurdo não viajar.
   */
  z.object({
    t: z.literal('match.brancaNaMao'),
    x: z.number().finite().min(-1).max(4),
    y: z.number().finite().min(-1).max(3),
  }),

  /**
   * A tacada, já quantizada.
   *
   * São exatamente os inteiros que vão para o replay. Não existe conversão no
   * meio do caminho onde cliente e servidor possam discordar por um bit.
   */
  z.object({
    t: z.literal('match.shoot'),
    angle: z.number().int().min(0).max(65_535),
    power: z.number().int().min(0).max(255),
    spinX: z.number().int().min(-127).max(127),
    spinY: z.number().int().min(-127).max(127),
    /**
     * Bola e caçapa prometidas, quando a regra exige.
     *
     * Vai JUNTO da tacada porque é isso que ela é: uma promessa feita antes de
     * jogar. Numa mensagem separada, o jogador poderia declarar depois de ver
     * o resultado.
     */
    call: z
      .object({ ball: z.number().int().min(0).max(15), pocket: z.number().int().min(0).max(5) })
      .optional(),
  }),

  /**
   * A tacada da disputa da saída, o mini jogo que decide quem quebra.
   *
   * Mensagem própria, e não um `match.shoot`, porque é outra mesa: uma
   * `match.shoot` atrasada da disputa, chegando depois da quebra, seria
   * aplicada à partida.
   */
  z.object({
    t: z.literal('match.disputa.shoot'),
    angle: z.number().int().min(0).max(65_535),
    power: z.number().int().min(0).max(255),
    spinX: z.number().int().min(-127).max(127),
    spinY: z.number().int().min(-127).max(127),
  }),

  /**
   * Onde a branca vai, numa bola na mão.
   *
   * Em metros. O servidor quantiza e devolve a posição efetiva — é ela que
   * fica gravada no replay e que a física recebe dos dois lados.
   */
  z.object({
    t: z.literal('match.place'),
    x: z.number().finite(),
    y: z.number().finite(),
  }),

  /** Escolha numa decisão aberta pelas regras. */
  z.object({ t: z.literal('match.decide'), option: z.number().int().min(0).max(255) }),
  z.object({ t: z.literal('match.forfeit') }),

  /**
   * "Minha mesa divergiu da sua: manda ela de novo."
   *
   * O cliente detecta a divergência sozinho — pelo hash do `match.sync` ou
   * pela conferência da própria tacada — mas até aqui só conseguia AVISAR o
   * jogador e seguir jogando numa mesa que não era a da partida. A resposta é
   * a mesma sequência da reconexão (`match.begin`, `match.start`,
   * `match.history`…): o cliente rearma a mesa do zero e avança pelo
   * histórico, que é a verdade do servidor. Não existe um segundo formato de
   * "estado da mesa" para divergir do primeiro.
   */
  z.object({
    t: z.literal('match.resync'),
    /**
     * O que o cliente viu, para o servidor guardar ao lado do que ELE viu.
     *
     * Opcional: um cliente antigo não manda, e a mesa volta do mesmo jeito —
     * pedir a mesa de volta nunca pode depender de conseguir relatar.
     *
     * É DADO DE INVESTIGAÇÃO, não verdade: vem de um navegador que pode estar
     * modificado. O servidor guarda como veio e não decide nada com isso —
     * quem decide a partida continua sendo o replay dele. Por isso o campo é
     * um texto opaco com teto de tamanho, e não um formato que o servidor
     * interprete: nada aqui alimenta a partida.
     */
    dossie: z.string().max(64_000).optional(),
  }),

  // ---------------------------------------------------------- espectador

  /**
   * Pede para assistir a partida de uma sala.
   *
   * Autenticado como tudo o mais, mas sem dinheiro: quem assiste não deposita e
   * não entra na conta `Game`. O servidor só aceita se a sala foi criada com
   * `spectators` ligado, e o que se recebe depois são os MESMOS eventos que os
   * jogadores recebem — nenhum a mais, e sem poder responder a nenhum deles.
   */
  z.object({
    t: z.literal('match.watch'),
    roomId: z.string().max(TETO_ID),
    password: z.string().max(TETO_SENHA).optional(),
  }),

  /**
   * Qual sala tem este código de convite? Responde `room.byCode` ou erro.
   *
   * Uma mensagem própria, e não `room.self` (que é a MINHA sala) nem
   * `lobby.upsert` (que poria na lista uma sala que a lista não mostra —
   * fechada sem plateia, por exemplo). Quem pergunta recebe a sala e decide
   * o que abrir com ela.
   */
  z.object({ t: z.literal('lobby.resolveCode'), code: z.string().max(TETO_CODIGO) }),

  /**
   * Escolhe (ou troca) o apelido. Responde `perfil.self`, ou erro com evento.
   * Teto frouxo no schema pelo mesmo motivo da senha: é `conferirApelido`, no
   * servidor, que recusa com o motivo certo. Ver `TETO_APELIDO`.
   */
  z.object({ t: z.literal('perfil.set'), nickname: z.string().max(TETO_APELIDO) }),

  // ------------------------------------------------------------- conversa

  /**
   * Uma frase para a mesa.
   *
   * O teto do schema é frouxo de propósito: `CHAT_MAX_CHARS` é regra de jogo, e
   * estourá-la merece um código próprio que a tela sabe explicar. O que este
   * `max` barra é outra coisa — um cliente hostil mandando megabytes —, e para
   * isso `bad_request` basta.
   */
  z.object({ t: z.literal('chat.send'), text: z.string().max(CHAT_MAX_CHARS * 5) }),

  // --------------------------------------------------------------------- voz
  z.object({ t: z.literal('voice.join') }),
  z.object({ t: z.literal('voice.leave') }),

  /**
   * Pede a cunhagem do taco público desta carteira.
   *
   * O pagamento é a ASSINATURA da transferência; o servidor vai à chain
   * conferir — o cliente não informa valor nem destino, porque não é nele que
   * se confia.
   */
  z.object({ t: z.literal('tacos.mint'), pagamento: z.string().min(64).max(96).optional() }),

  /**
   * Compra UM nível de UM atributo do taco `mint`.
   *
   * Um nível por mensagem, e não "o pacote que eu montei": cada nível tem
   * preço próprio na curva e um pagamento próprio na chain. O cliente não diz
   * quanto pagou nem qual nível está comprando — o servidor sabe em que nível
   * o taco está e cobra o da vez; o `pagamento` é só a ASSINATURA para ele
   * conferir na chain, como no mint.
   */
  z.object({
    t: z.literal('tacos.upgrade'),
    mint: z.string().min(32).max(64),
    atributo: AtributoSchema,
    pagamento: z.string().min(64).max(96).optional(),
  }),

  z.object({ t: z.literal('ping') }),

  ...MensagensDoClienteDeEvento,
])
export type ClientMessage = z.infer<typeof ClientMessage>

// ---------------------------------------------------------------- servidor →

/**
 * O que está em jogo agora, como código (`engine-rules` produz; o cliente
 * traduz). Acompanha o `status` em texto nas mensagens de tacada e de sync.
 * Parâmetros só numéricos: ids de bola e contagens — palavra é do cliente.
 */
export const StatusSchema = z.object({
  code: z.string(),
  params: z.record(z.number()).optional(),
})
export type StatusMessage = z.infer<typeof StatusSchema>

export const ServerMessage = z.discriminatedUnion('t', [
  /**
   * O modo manutenção mudou (ou, na conexão, está ligado). Com ele ligado não
   * se cria nem se entra em mesa ou evento; o que está em jogo segue.
   * `previstaPara` é quando a atualização deve acontecer (epoch ms), se quem
   * ligou disse.
   */
  z.object({
    t: z.literal('manutencao'),
    ligada: z.boolean(),
    previstaPara: z.number().nullable(),
  }),
  z.object({
    t: z.literal('hello'),
    minStake: z.string(),
    maxStake: z.string(),
    decimals: z.number(),
    symbol: z.string(),
    cluster: z.string(),
    programId: z.string(),
    /** Prazo on-chain da partida, em segundos. */
    matchTimeoutSeconds: z.number(),
    /** Se o servidor pode entregar SOL de teste (só em devnet). */
    faucetAvailable: z.boolean(),
    /** URL de faucet EXTERNO (testnet). `null` = o servidor banca com `/api/faucet`. */
    faucetUrl: z.string().nullable().optional(),
  }),
  z.object({
    t: z.literal('auth.ok'),
    address: z.string(),
    sessionToken: z.string(),
    expiresAt: z.number(),
  }),
  /** Saldo real da carteira on-chain, em lamports. */
  z.object({ t: z.literal('balance'), lamports: z.string() }),
  /** O apelido desta carteira: depois do login e em resposta a `perfil.set`. */
  z.object({ t: z.literal('perfil.self'), nickname: z.string().nullable() }),
  /**
   * Os tacos que ESTA carteira pode usar.
   *
   * Vem depois do login, e não na hora de jogar, para a escolha ser validada
   * ANTES de qualquer dinheiro entrar: escolher um taco, depositar e só então
   * descobrir que ele não vale seria o pior desfecho possível.
   *
   * É a resposta do SERVIDOR sobre a posse — hoje uma tabela, amanhã os NFTs
   * da carteira. O cliente mostra o que chegar aqui e nada além disso; se um
   * dia divergirem, quem manda continua sendo o servidor na hora da partida.
   */
  z.object({ t: z.literal('tacos.self'), tacos: z.array(TacoEmJogoSchema) }),

  /**
   * O estado atual do mint público de tacos.
   *
   * `precoLamports` é string porque lamports não cabem com segurança em
   * `number` no cliente (JS perde precisão acima de 2^53). `autoridade` vem
   * nula quando o mint está indisponível neste servidor.
   */
  z.object({
    t: z.literal('tacos.mint.estado'),
    precoLamports: z.string(),
    cunhados: z.number(),
    teto: z.number(),
    autoridade: z.string().nullable(),
    podeCunhar: z.boolean(),
    pendente: z.boolean(),
  }),
  /** A cunhagem desta carteira deu certo: o taco que acabou de nascer. */
  z.object({ t: z.literal('tacos.mint.ok'), taco: TacoEmJogoSchema }),

  /**
   * O nível foi comprado: o taco INTEIRO, já recalculado.
   *
   * Vai o taco todo, e não só o nível novo, porque um nível muda pontos,
   * params, gastos e o preço/motivo dos CINCO atributos (o orçamento é
   * compartilhado). Mandar o delta obrigaria o cliente a refazer a conta —
   * que é exatamente o que o servidor existe para não deixar acontecer.
   */
  z.object({ t: z.literal('tacos.upgrade.ok'), taco: TacoEmJogoSchema }),

  /** Resposta a `lobby.reserve` / `lobby.requestJoin`: assine e envie. */
  z.object({
    t: z.literal('deposit.required'),
    action: z.enum(['create', 'join']),
    matchId: z.string(),
    stake: z.string(),
    roomId: z.string().nullable(),
    timeoutSeconds: z.number(),
  }),

  z.object({ t: z.literal('lobby.state'), rooms: z.array(Room) }),
  z.object({ t: z.literal('lobby.upsert'), room: Room }),
  z.object({ t: z.literal('lobby.remove'), roomId: z.string() }),
  z.object({ t: z.literal('room.self'), room: Room.nullable() }),
  /** Resposta a `lobby.resolveCode`. `code` é o pedido, já normalizado, para casar a resposta. */
  z.object({ t: z.literal('room.byCode'), code: z.string(), room: Room }),
  // ------------------------------------------------------------- partida

  /** A partida existe; mande o compromisso. */
  z.object({
    t: z.literal('match.begin'),
    matchId: z.string(),
    mode: z.enum(['eightball', 'sinuca']),
    /** Índice deste jogador: 0 ou 1. */
    you: z.union([z.literal(0), z.literal(1)]),
    opponent: z.string(),
    revealDeadline: z.number(),
    /** Apelidos dos dois, na ordem dos índices. Opcional para servidor antigo. */
    names: PlayerNames.optional(),
  }),

  /** Os dois se comprometeram; pode revelar. */
  z.object({ t: z.literal('match.reveal.open') }),

  /**
   * Você está ASSISTINDO esta partida.
   *
   * Faz para o espectador o que `match.begin` faz para o jogador — diz qual é a
   * partida e quem joga —, sem o `you` e sem o pedido de compromisso, porque
   * não há índice que o espectador ocupe e nada que ele deva sortear. Uma
   * mensagem própria, e não um `match.begin` com `you: null`, para que nenhum
   * cliente antigo responda a ela mandando um compromisso.
   */
  z.object({
    t: z.literal('match.watching'),
    matchId: z.string(),
    mode: z.enum(['eightball', 'sinuca']),
    players: z.tuple([z.string(), z.string()]),
    names: PlayerNames.optional(),
  }),

  /**
   * Histórico da partida, para quem chega no meio.
   *
   * Vai o replay inteiro — seed, tacos, tacadas e decisões — porque é o mesmo
   * formato que o cliente já sabe ler e que vai para a blockchain. Reenviar só
   * o seed deixaria a mesa remontada na QUEBRA, e o jogador veria uma partida
   * que não é a que está acontecendo.
   */
  z.object({
    t: z.literal('match.history'),
    replay: z.string(),
    turn: z.union([z.literal(0), z.literal(1)]).nullable(),
    deadline: z.number().nullable(),
  }),

  /**
   * A disputa da saída está aberta: o mini jogo que decide quem quebra.
   *
   * Vão o seed e os tacos, como no `match.start`, porque é com eles que o
   * cliente arma a MESMA mesa da disputa (`layoutDaDisputa`) e simula as
   * tacadas igual ao servidor. Enviado ao abrir e a quem chega no meio; por
   * isso vai o estado inteiro, e não só "começou".
   */
  z.object({
    t: z.literal('match.disputa'),
    seed: z.string().regex(/^[0-9a-f]{64}$/),
    cues: z.tuple([CueEmJogo, CueEmJogo]),
    names: PlayerNames.optional(),
    rodada: z.number().int().min(0),
    /** Quem abre a rodada. */
    primeiro: z.union([z.literal(0), z.literal(1)]),
    /** De quem é a tacada agora. */
    vez: z.union([z.literal(0), z.literal(1)]),
    /** Resultado de quem já tacou nesta rodada; null é o pior. */
    medidas: z.array(z.number().nullable()),
    deadline: z.number().nullable(),
  }),

  /**
   * Uma tacada da disputa aconteceu.
   *
   * O cliente simula a mesma tacada na mesma mesa e mostra onde a bola parou.
   * `medida` é a do servidor: é ela que vale, e a do cliente só confere.
   * Quando a tacada encerra a disputa, `vencedor` vem preenchido e um
   * `match.start` chega logo em seguida.
   */
  z.object({
    t: z.literal('match.disputa.shot'),
    by: z.union([z.literal(0), z.literal(1)]),
    angle: z.number().int(),
    power: z.number().int(),
    spinX: z.number().int(),
    spinY: z.number().int(),
    /** A tacada foi do relógio: quem estava na vez não tacou. */
    byClock: z.boolean().optional(),
    /** A rodada em que esta tacada foi dada. */
    rodada: z.number().int().min(0),
    medida: z.number().nullable(),
    /** A rodada depois desta tacada (maior que `rodada` quando houve empate). */
    proximaRodada: z.number().int().min(0),
    vez: z.union([z.literal(0), z.literal(1)]).nullable(),
    vencedor: z.union([z.literal(0), z.literal(1)]).nullable(),
    deadline: z.number().nullable(),
  }),

  /** A quebra está definida e a partida começou. */
  z.object({
    t: z.literal('match.start'),
    /**
     * Quem quebra: o vencedor da disputa da saída.
     *
     * Não é o mesmo que `turn`, que é a vez AGORA — para quem reconecta no
     * meio da partida, as duas coisas diferem. O cliente arma as regras com
     * este. Ausente num servidor antigo: quebra o jogador 0, como era.
     */
    quebra: z.union([z.literal(0), z.literal(1)]).optional(),
    /**
     * Quantas tacadas a disputa da saída gastou.
     *
     * Elas saem do MESMO teto de tacadas do replay que as da partida, e o
     * cliente, que arma a mesa da partida do zero, precisa descontá-las para
     * não oferecer uma tacada que o servidor já não consegue gravar. Ausente
     * num servidor antigo: zero.
     */
    disputa: z.number().int().min(0).max(15).optional(),
    /** Seed da quebra em hex — o cliente arma a mesma mesa. */
    seed: z.string().regex(/^[0-9a-f]{64}$/),
    /**
     * O taco de cada jogador, na ordem dos índices.
     *
     * OS NÚMEROS VIAJAM, e não só o id. O taco entra na física, e o cliente
     * arma a mesma mesa que o servidor a partir do seed — se ele derivasse os
     * atributos de um catálogo próprio, uma versão diferente entre os dois
     * lados faria as simulações renderem posições diferentes e a mesa passaria
     * a "reiniciar" a cada correção. O servidor é a autoridade sobre o taco,
     * então é ele quem manda os valores com que se joga.
     *
     * O `id` vem junto só para a SKIN: é ele que diz qual arte desenhar, e sem
     * ele ninguém vê o taco do adversário.
     *
     * Opcional porque um servidor antigo não manda — nesse caso o cliente cai
     * no taco padrão dos dois lados, que é como era antes de existir catálogo.
     */
    cues: z.tuple([CueEmJogo, CueEmJogo]).optional(),
    /** Apelidos dos dois, na ordem dos índices. Opcional para servidor antigo. */
    names: PlayerNames.optional(),
    turn: z.union([z.literal(0), z.literal(1)]),
    /**
     * Prazo da tacada, ou `null` quando não há um.
     *
     * NULÁVEL, e as três mensagens que o declaravam obrigatório mentiam por
     * construção: o servidor não tem prazo em alguns instantes e era forçado a
     * inventar `0`. Zero não é "sem prazo" — vira "0s" na tela, e como o
     * relógio do cliente ignora `null` sem redesenhar, o texto ficava
     * congelado em "0s" pelo resto da partida.
     */
    deadline: z.number().nullable(),
  }),

  /**
   * Uma tacada aconteceu.
   *
   * Vai a tacada e o hash do estado resultante. O cliente simula a MESMA
   * tacada localmente e compara: se divergir, ele sabe na hora, em vez de
   * mostrar uma mesa que não é a real até o fim da partida.
   */
  z.object({
    t: z.literal('match.shot'),
    by: z.union([z.literal(0), z.literal(1)]),
    angle: z.number().int(),
    power: z.number().int(),
    spinX: z.number().int(),
    spinY: z.number().int(),
    stateHash: z.string(),
    /**
     * A tacada foi dada pelo RELÓGIO, não pelo jogador.
     *
     * Muda o que o cliente faz: numa tacada própria ele já aplicou por
     * previsão e só confere o hash, mas a do relógio ninguém previu —
     * inclusive a do próprio jogador que deixou o tempo acabar.
     */
    byClock: z.boolean().optional(),
    /**
     * A caçapa que quem tacou DECLAROU, quando a regra exigia.
     *
     * Sem ela o cliente julgava a tacada do adversário com a caçapa padrão
     * (a 0), e quando a 8 caía na declarada o servidor dava a vitória enquanto
     * a mesa do outro jogador dava a derrota — dois vencedores para a mesma
     * tacada. É a mesma declaração que fica gravada no replay.
     */
    call: z
      .object({ ball: z.number().int().min(0).max(15), pocket: z.number().int().min(0).max(5) })
      .optional(),
    turn: z.union([z.literal(0), z.literal(1)]).nullable(),
    deadline: z.number().nullable(),
    status: z.string(),
    statusCode: StatusSchema.optional(),
    score: z.tuple([z.number(), z.number()]).nullable(),
    onTable: z.array(z.number()),
  }),

  /**
   * Estado de CONTROLE da partida, repetido de tempos em tempos.
   *
   * O que vai aqui é de quem é a vez, em que fase está, o que a regra exige
   * agora — e o hash da mesa. NÃO vão as posições das bolas, e a omissão é
   * deliberada: a física é determinística e roda igual dos dois lados, então
   * mandar posições seria repetir a cada segundo algo que o cliente já sabe
   * calcular. Pior: esconderia uma divergência em vez de denunciá-la, porque a
   * mesa seria corrigida por cima e ninguém saberia que os dois discordaram.
   *
   * O `stateHash` faz o contrário — se ele diferir do local, houve divergência
   * REAL de física, e isso precisa aparecer, não ser encoberto.
   *
   * O que isto conserta é a outra classe de problema: o cliente perder um
   * evento de controle e ficar com a tela travada, esperando um painel que não
   * vem. Com a repetição, um estado desencontrado se corrige sozinho no
   * próximo segundo em vez de durar até o prazo da partida.
   */
  z.object({
    t: z.literal('match.sync'),
    turn: z.union([z.literal(0), z.literal(1)]).nullable(),
    deadline: z.number().nullable(),
    /**
     * Que horas são PARA O SERVIDOR, no instante em que ele mandou isto.
     *
     * `deadline` é um carimbo absoluto do relógio dele, e o cliente comparava
     * com o relógio do navegador — duas réguas diferentes. Relógio de PC anda,
     * fuso vem mal configurado, máquina que dormiu acorda atrasada: com o
     * navegador 30 s adiantado, a mesa mostrava 30 s a menos e o relógio sumia
     * antes da hora.
     *
     * Com este campo, a diferença entre os dois relógios é medida e descontada.
     * Opcional para um servidor antigo continuar parseando; o cliente trata a
     * ausência como defasagem zero, que é o comportamento de antes.
     */
    serverNow: z.number().optional(),
    phase: z.string(),
    callRequired: z.boolean(),
    ballInHand: z
      .object({ who: z.union([z.literal(0), z.literal(1)]), region: z.string() })
      .nullable(),
    stateHash: z.string(),
    status: z.string(),
    statusCode: StatusSchema.optional(),
    score: z.tuple([z.number(), z.number()]).nullable(),
    onTable: z.array(z.number()),
    /** Os apelidos, repetidos: é por aqui que uma troca no meio da partida chega. */
    names: PlayerNames.optional(),
    /**
     * Quantos estão assistindo agora.
     *
     * Vai no `sync` porque é o único número da partida que muda sem jogada
     * nenhuma: gente entra e sai. Os jogadores merecem saber que há plateia —
     * é a diferença entre uma mesa fechada e uma mesa com gente em volta.
     * Opcional para um servidor antigo; ausente vale zero.
     */
    spectators: z.number().int().nonnegative().optional(),
  }),

  /**
   * A mira do ADVERSÁRIO, retransmitida.
   *
   * Chega várias vezes por segundo enquanto ele se posiciona e some quando a
   * tacada sai. É a única mensagem do protocolo que pode ser perdida sem
   * consequência: nada depende dela, ela só desenha.
   */
  z.object({
    t: z.literal('match.aim'),
    by: z.union([z.literal(0), z.literal(1)]),
    angle: z.number().int(),
    power: z.number().int(),
    /** A caçapa que ele declarou, ou nulo enquanto não declarou. */
    pocket: z.number().int().min(0).max(5).nullable().optional(),
  }),

  /** O adversário está levando a branca, numa bola na mão. Só desenho. */
  z.object({
    t: z.literal('match.brancaNaMao'),
    by: z.union([z.literal(0), z.literal(1)]),
    x: z.number(),
    y: z.number(),
  }),

  /** A tacada da vez exige declarar bola e caçapa. */
  z.object({
    t: z.literal('match.callRequired'),
    who: z.union([z.literal(0), z.literal(1)]),
    ball: z.number().int(),
  }),

  /** Alguém tem bola na mão e precisa colocá-la antes de jogar. */
  z.object({
    t: z.literal('match.ballInHand'),
    who: z.union([z.literal(0), z.literal(1)]),
    region: z.enum(['anywhere', 'kitchen']),
    /**
     * Prazo da tacada, ou `null` quando não há um.
     *
     * NULÁVEL, e as três mensagens que o declaravam obrigatório mentiam por
     * construção: o servidor não tem prazo em alguns instantes e era forçado a
     * inventar `0`. Zero não é "sem prazo" — vira "0s" na tela, e como o
     * relógio do cliente ignora `null` sem redesenhar, o texto ficava
     * congelado em "0s" pelo resto da partida.
     */
    deadline: z.number().nullable(),
  }),

  /** A branca foi colocada. A posição já vem quantizada. */
  z.object({
    t: z.literal('match.placed'),
    by: z.union([z.literal(0), z.literal(1)]),
    x: z.number(),
    y: z.number(),
    /** Posta pelo relógio no ponto de saque, e não escolhida pelo jogador. */
    byClock: z.boolean().optional(),
    deadline: z.number().nullable(),
  }),

  /** Alguém precisa escolher antes da próxima tacada. */
  z.object({
    t: z.literal('match.decision'),
    chooser: z.union([z.literal(0), z.literal(1)]),
    kind: z.string(),
    /**
     * As opções VÁLIDAS, cada uma com o índice canônico.
     *
     * O índice é o que vai gravado no replay; a posição na lista não serve,
     * porque a lista mostra só o que a regra permite naquela situação.
     */
    options: z.array(z.object({ index: z.number().int(), label: z.string() })),
    /**
     * Prazo da tacada, ou `null` quando não há um.
     *
     * NULÁVEL, e as três mensagens que o declaravam obrigatório mentiam por
     * construção: o servidor não tem prazo em alguns instantes e era forçado a
     * inventar `0`. Zero não é "sem prazo" — vira "0s" na tela, e como o
     * relógio do cliente ignora `null` sem redesenhar, o texto ficava
     * congelado em "0s" pelo resto da partida.
     */
    deadline: z.number().nullable(),
  }),

  /** A escolha foi aplicada. `rerack` avisa que o triângulo foi rearmado. */
  z.object({
    t: z.literal('match.decided'),
    chooser: z.union([z.literal(0), z.literal(1)]),
    option: z.number(),
    rerack: z.boolean(),
    turn: z.union([z.literal(0), z.literal(1)]).nullable(),
    deadline: z.number().nullable(),
  }),

  /** O adversário caiu; contando a tolerância. */
  z.object({ t: z.literal('match.opponentOffline'), until: z.number() }),
  z.object({ t: z.literal('match.opponentOnline') }),

  z.object({
    t: z.literal('match.end'),
    winner: z.union([z.literal(0), z.literal(1)]).nullable(),
    /**
     * Como a partida acabou.
     *
     * Não existe "abandono": quem cai perde os TURNOS, não a partida. E
     * "replay cheio" anula, porque sem espaço para gravar não há como provar
     * o resultado — e um vencedor que o replay não sustenta valeria menos que
     * nenhum.
     */
    reason: z.enum(['regras', 'desistência', 'tempo', 'replay cheio']),
    /**
     * QUAL regra encerrou, como código das regras (por exemplo
     * `eight-ball-wrong-pocket`, `three-fouls`). Opcional para servidores
     * antigos; null quando o fim não veio de regra.
     */
    motivo: z.string().nullable().optional(),
    /** Replay em hex, para o cliente conferir por conta própria. */
    replay: z.string(),
  }),

  /**
   * A liquidação foi para a blockchain.
   *
   * Chega DEPOIS do `match.end` porque a transação leva alguns segundos e o
   * jogador não deve ficar olhando uma tela parada até ela confirmar. Traz a
   * assinatura para ele conferir o pagamento no explorer — é o ponto de o
   * escrow ser on-chain.
   */
  z.object({
    t: z.literal('match.settled'),
    signature: z.string().nullable(),
    /** Motivo, quando não deu para liquidar. Texto de compatibilidade. */
    reason: z.string().nullable(),
    /** O mesmo motivo como CÓDIGO; é o que o cliente traduz. */
    evento: EventoSchema.optional(),
  }),

  /*
   * Erro para o jogador.
   *
   * `code` é a classe do erro, e é o que o cliente usa para DECIDIR (um
   * `nonce_invalid` apaga a sessão guardada). `evento` é o que aconteceu, fino
   * o bastante para virar uma frase: `wrong_phase` cobre seis situações
   * diferentes, e o jogador precisa saber qual. `message` fica por
   * compatibilidade — cliente que conhece o evento o prefere.
   */
  z.object({
    t: z.literal('error'),
    code: ErrorCode,
    message: z.string(),
    evento: EventoSchema.optional(),
  }),

  /**
   * Uma frase dita na mesa.
   *
   * `by` é o índice do jogador, ou `null` quando foi um espectador — e então
   * `who` é o endereço dele abreviado, que é a única identidade que existe
   * aqui. `at` é a hora do SERVIDOR: o relógio do cliente não vale como ordem.
   *
   * Não persiste. Nem no banco nem no replay: é conversa, não jogo, e um
   * replay que a carregasse deixaria de provar só o que precisa provar.
   */
  z.object({
    t: z.literal('chat.message'),
    by: z.union([z.literal(0), z.literal(1)]).nullable(),
    who: z.string(),
    text: z.string().max(CHAT_MAX_CHARS),
    at: z.number(),
  }),

  /**
   * As últimas frases da mesa, para quem chega (ou volta) no meio.
   *
   * A conversa começa na SALA, antes de a partida nascer, e continua nela: a
   * chave é o id da sala, que é o `match_id`. Vai ao entrar na sala, ao
   * reconectar e ao começar a assistir. Em memória, só: um servidor que
   * reinicia esquece, e isso é correto.
   */
  z.object({
    t: z.literal('chat.history'),
    messages: z.array(
      z.object({
        by: z.union([z.literal(0), z.literal(1)]).nullable(),
        who: z.string(),
        text: z.string().max(CHAT_MAX_CHARS),
        at: z.number(),
      }),
    ),
  }),

  z.object({ t: z.literal('pong') }),

  /**
   * O passe de voz: onde conectar e o que se pode fazer lá.
   *
   * `canPublish` é a linha inteira entre falar e escutar, e ela é decidida no
   * SERVIDOR. O cliente a lê só para desenhar (ou não) o botão de microfone —
   * mentir sobre ela não dá microfone a ninguém, porque quem confere é o
   * servidor de mídia contra a assinatura deste passe.
   *
   * `expiresAt` existe para o cliente renovar ANTES de cair, e não para ele
   * decidir coisa alguma: a validade real é a que está assinada dentro do
   * token.
   */
  z.object({
    t: z.literal('voice.token'),
    url: z.string(),
    token: z.string(),
    canPublish: z.boolean(),
    expiresAt: z.number(),
  }),

  ...MensagensDoServidorDeEvento,
])
export type ServerMessage = z.infer<typeof ServerMessage>

// ---------------------------------------------------------------- utilidades

/**
 * Mensagem de login. O servidor reconstrói esta string a partir do nonce que
 * ele mesmo emitiu e verifica a assinatura contra ela. Mudar o formato quebra
 * o login e exige versionar.
 */
export function buildLoginMessage(host: string, address: string, nonce: string): string {
  return [
    `${host} quer autenticar sua carteira.`,
    '',
    'Assinar esta mensagem é gratuito e NÃO autoriza nenhuma transação.',
    '',
    `Carteira: ${address}`,
    `Nonce: ${nonce}`,
  ].join('\n')
}

export function formatAmount(raw: string | bigint, decimals = DECIMALS): string {
  const value = BigInt(raw)
  const base = 10n ** BigInt(decimals)
  const whole = value / base
  const frac = (value % base).toString().padStart(decimals, '0').replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : whole.toString()
}

export function parseAmount(input: string, decimals = DECIMALS): bigint | null {
  const trimmed = input.trim().replace(',', '.')
  if (!/^\d*\.?\d*$/.test(trimmed) || trimmed === '' || trimmed === '.') return null
  const [whole = '0', frac = ''] = trimmed.split('.')
  if (frac.length > decimals) return null
  return BigInt(whole + frac.padEnd(decimals, '0'))
}
export * from './engines'
export * from './eventos'
export * from './eventos-de-mesa'
export * from './valores'
export * from './prazos'
export * from './senha'
export * from './convite'
export * from './apelido'
export * from './historico'
export * from './ranking'
export * from './perfil-jogador'
